import React from 'react';
import { useForm } from 'react-hook-form';
import { useMutation } from '@apollo/client/react';
import DraggableDialog from '@components-clean/core/draggable-dialog';
import { TextAreaController } from '@components-clean/core/text-area';
import { FormInputs } from '@pages-clean/risk-assessment/risk-log/dialogs/delete-risk-dialog';
import style from '@pages-clean/risk-assessment/risk-log/dialogs/style.module.scss';
import { useTranslation } from '@hooks/index';
import { ShowError, ShowSuccess } from '@utils/notification';
import { ReportDefinitionFields } from '@queries/generated/fragments.generated';
import {
    deleteReportDefinition,
    deleteReportDefinitionVariables,
} from '@queries/generated/report-definition.generated';
import { DELETE_CUSTOM_REPORT, GET_REPORT_DEFINITIONS } from '@queries/report-definition';

export interface DeleteExcelListingsConfigDialogProps {
    onClose: () => void;
    reportDefinition?: ReportDefinitionFields;
}

const DeleteExcelListingsConfigDialog = ({ onClose, reportDefinition }: DeleteExcelListingsConfigDialogProps) => {
    const { t } = useTranslation('ram', { keyPrefix: 'reporting.excelListings.dialogs.deleteExcelListingsConfig' });
    const { t: tCommon } = useTranslation('common');
    const {
        control,
        handleSubmit,
        formState: { isValid, isDirty, isSubmitting },
    } = useForm<FormInputs>({
        mode: 'all',
        defaultValues: {
            deleteReason: null,
        },
    });

    const [deleteReport] = useMutation<deleteReportDefinition, deleteReportDefinitionVariables>(DELETE_CUSTOM_REPORT);

    const onDelete = async (data: FormInputs) => {
        deleteReport({
            variables: {
                id: reportDefinition?.id,
                reason: data.deleteReason,
            },
            refetchQueries: [
                {
                    query: GET_REPORT_DEFINITIONS,
                },
            ],
        })
            .then((response) => {
                if (!response?.data || response?.data?.deleteReportDefinition === null) {
                    ShowError(tCommon('errorMessage'));
                    return;
                }
                ShowSuccess(tCommon('actionsSuccessfulToast'));
                onClose();
            })
            .catch(() => {
                ShowError(tCommon('errorMessage'));
            });
    };

    return (
        <DraggableDialog
            dataTestId="delete-excel-listings-config-dialog"
            onSubmit={() => handleSubmit(onDelete)()}
            onClose={onClose}
            title={`${t('title')}: ${reportDefinition?.recordId}`}
            initialWidth={475}
            initialHeight={280}
            height={null}
            primaryBtnLabel={tCommon('delete')}
            primaryBtnDisabled={!isValid || !isDirty || isSubmitting}
        >
            <div className="k-h-full k-w-full k-d-flex k-flex-column k-gap-2">
                <TextAreaController
                    className={style.textAreaOverrides}
                    maxLength={3000}
                    control={control}
                    name="deleteReason"
                    required
                    data-testid="excel-config-delete-reason"
                    label={`${t('deleteReasonLabel')} *`}
                />
            </div>
        </DraggableDialog>
    );
};

export default DeleteExcelListingsConfigDialog;
